/**
 * GET /api/v1/venues/:venueId/workshops/stats — the venue Workshops tab's KPI
 * aggregates, including the recurring cadence the venue runs on. Split out of
 * `workshops.ts` to keep that file under its size cap; re-exported from there
 * so callers keep one import path.
 */
import { apiFetch } from '../lib/apiFetch';

import type { VenueCadence } from './workshop-series.types';

/** Aggregate stats for one venue, GET /venues/:venueId/workshops/stats
 *  (BON-168). */
export interface VenueWorkshopStats {
  pastWorkshops: number;
  futureWorkshops: number;
  totalTicketsSold: number;
  /** Average fill across past workshops, 0..100. Null when the venue has no
   *  past workshops with capacity > 0. */
  avgFill: number | null;
  /** ISO date (or datetime) of the first workshop ever held here. Null when
   *  the venue has never hosted one. Rendered as "Venue Since". */
  venueSince: string | null;
  /** null when no recurring series at the venue has a workshop still
   *  to come. Optional only as deploy-skew defence — an older API omits it. */
  cadence?: VenueCadence | null;
}

export interface FetchVenueWorkshopStatsParams {
  venueId: string;
}

/** GET /api/v1/venues/:venueId/workshops/stats (BON-168). */
export async function fetchVenueWorkshopStats({
  venueId,
}: FetchVenueWorkshopStatsParams): Promise<VenueWorkshopStats> {
  const res = await apiFetch(`/api/v1/venues/${venueId}/workshops/stats`);
  if (!res.ok) {
    throw new Error(`Failed to fetch venue workshop stats: ${res.status}`);
  }
  return res.json();
}
